import { CanActivateFn, Router, Routes } from '@angular/router';
import { RegisterComponent } from './auth/register/register.component';
import { SignInComponent } from './auth/sign-in/sign-in.component';
import { LayoutComponent } from './main/layout/layout.component';
import { DashboardComponent } from './main/dashboard/dashboard.component';
import { EventDetailsComponent } from './main/event-details/event-details.component';
import { MeterDetailsComponent } from './main/meter-details/meter-details.component';
import { WalletComponent } from './main/wallet/wallet.component';
import { ProfileComponent } from './main/profile/profile.component';
import { LandingComponent } from './main/landing/landing.component';
import { AuthComponent } from './auth/auth/auth.component';
import { Component, inject } from '@angular/core';
import { UtilService } from './services/util.service';
import { FaqComponent } from './faq/faq.component';
import { OnboardMeterComponent } from './auth/onboard-meter.component';
import { DfsService } from './services/dfs.service';
import { map, Observable, of, tap, switchMap, catchError, from } from 'rxjs';
import { UserInfo, UserInfoResponse } from './models/user.models';
import { LoginService } from './services/login.service';
import { Location } from '@angular/common';

@Component({
  selector: 'app-root-redirect',
  template: '',
  styles: []
})
export class AppRouteRedirectComponent { 
  constructor() {
    // This component is used to redirect the root path
  }
}

const tpiGuard = (next: any, state: any): Observable<boolean> => {
  const location = inject(Location);
  const loginService = inject(LoginService);

  return loginService.getTpiStatus().pipe(
    map(isTpi => {
      if (isTpi) {
        // location.back();
        return false;
      }
      return true;
    })
  );
}

export const authGuard: CanActivateFn = (route, state) => {
  const loginService = inject(LoginService);
  const router = inject(Router);

  return from(loginService.isLoggedInAsync()).pipe(
    map(isLoggedIn => {
      if (isLoggedIn) {
        return true;
      } else {
        // Redirect to sign-in page if not logged in
        router.navigate(['/signin'], {queryParams: route.queryParams});
        return false;
      }
    }),
    catchError(error => {
      console.error('Auth guard error:', error);
      // On error, redirect to landing page and deny access
      router.navigate(['/signin'], {queryParams: route.queryParams});
      return of(false);
    })
  );
};

export const rootGuard: CanActivateFn = (route, state) => {
  const loginService = inject(LoginService);
  const router = inject(Router);

  return from(loginService.isLoggedInAsync()).pipe(
    map(isLoggedIn => {
      if (isLoggedIn) {
        // User is logged in, redirect to dashboard
        router.navigate(['/dashboard']);
        return false;
      } else {
        // User is not logged in, redirect to landing
        router.navigate(['/landing']);
        return false;
      }
    }),
    catchError(error => {
      console.error('Root guard error:', error);
      // On error, redirect to landing page
      router.navigate(['/landing']);
      return of(false);
    })
  );
};

const utmTpiResolver =  (next: any, state: any): Observable<{utm: string; tpi: boolean; email: string}> => {
  const loginService = inject(LoginService);

  return loginService.loginUserEmail.pipe(
    switchMap(email => {
      if (!email) {
        return of({ utm: '', tpi: false, email: '' });
      }

      return loginService.getUserDetails(email, true).then(userDetails => {
        const isTpi = userDetails?.isTPI === 1;
        const utm = userDetails?.UtmSource || '';
        return { utm, tpi: isTpi, email };
      }).catch(() => {
        return { utm: '', tpi: false, email };
      });
    })
  );
}



export const routes: Routes = [
  { path: 'signin', component: SignInComponent },
  { path: 'register', component: RegisterComponent },
  { path: 'landing', component: LandingComponent },
  { path: 'auth', component: AuthComponent },
  { path: 'faq', component: FaqComponent },

  {
    path: '',
    component: LayoutComponent,
    resolve: { tpiData: utmTpiResolver }, // Resolver to block route until data is fetched
    children: [
      { path: '', component: AppRouteRedirectComponent, canActivate: [rootGuard] },
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [authGuard],
      },
      { path: 'wallet', component: WalletComponent, canActivate: [authGuard, tpiGuard] },
      {
        path: 'meter-details',
        component: MeterDetailsComponent,
        canActivate: [authGuard],
      },
      {
        path: 'event-details',
        component: EventDetailsComponent,
        canActivate: [authGuard],
      },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [authGuard],
      },
      {
        path: 'onboard-meter',
        component: OnboardMeterComponent,
        canActivate: [authGuard],
      },
    ],
  },

  { path: '**', redirectTo: '/signin' },
];